import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { listRunsByConfig, LinkCheckerRun, getConfig, LinkCheckerConfig, triggerCheck, updateAllowedFailures, AllowedFailure } from '../repository/linkCheckerRepository';
import RunDetailsComponent from '../components/RunDetailsComponent';

const ConfigRunsPage: React.FC = () => {
  const { configId } = useParams<{ configId: string }>();
  const [config, setConfig] = useState<LinkCheckerConfig | null>(null);
  const [runs, setRuns] = useState<LinkCheckerRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [newAllowedUrl, setNewAllowedUrl] = useState('');
  const [newAllowedStatusCode, setNewAllowedStatusCode] = useState('');
  const [allowlistSaving, setAllowlistSaving] = useState(false);

  useEffect(() => {
    if (configId) {
      fetchConfigAndRuns(configId);
    }
  }, [configId]);

  const fetchConfigAndRuns = async (uuid: string) => {
    setLoading(true);
    setError(null);
    try {
      const fetchedConfig = await getConfig(uuid);
      setConfig(fetchedConfig);
      const fetchedRuns = await listRunsByConfig(uuid);
      setRuns(fetchedRuns);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch configuration and runs.');
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerCheck = async () => {
    if (!configId) return;
    setTriggering(true);
    setError(null);
    try {
      const newRun = await triggerCheck(configId);
      setRuns((prev) => [newRun, ...prev]);
    } catch (err: any) {
      setError(err.message || 'Failed to trigger check.');
    } finally {
      setTriggering(false);
    }
  };

  const handleAddAllowedFailure = async () => {
    if (!config || !configId || !newAllowedUrl || !newAllowedStatusCode) return;
    const statusCode = parseInt(newAllowedStatusCode, 10);
    if (isNaN(statusCode)) return;

    const current = config.allowed_failures || [];
    const already = current.some(e => e.url === newAllowedUrl && e.status_code === statusCode);
    if (already) return;

    setAllowlistSaving(true);
    try {
      const updated = await updateAllowedFailures(configId, [...current, { url: newAllowedUrl, status_code: statusCode }]);
      setConfig(updated);
      setNewAllowedUrl('');
      setNewAllowedStatusCode('');
    } catch (err: any) {
      setError(err.message || 'Failed to update allowed failures.');
    } finally {
      setAllowlistSaving(false);
    }
  };

  const handleRemoveAllowedFailure = async (entry: AllowedFailure) => {
    if (!config || !configId) return;
    const current = config.allowed_failures || [];
    const updated = current.filter(e => !(e.url === entry.url && e.status_code === entry.status_code));
    setAllowlistSaving(true);
    try {
      const updatedConfig = await updateAllowedFailures(configId, updated);
      setConfig(updatedConfig);
    } catch (err: any) {
      setError(err.message || 'Failed to update allowed failures.');
    } finally {
      setAllowlistSaving(false);
    }
  };

  if (loading) return <div className="app-shell"><main className="app-content"><p>Loading runs...</p></main></div>;
  if (error) return <div className="app-shell"><main className="app-content"><div className="alert error">Error: {error}</div></main></div>;
  if (!config) return <div className="app-shell"><main className="app-content"><p>Configuration not found.</p></main></div>;

  return (
    <div className="app-shell">
      <header className="app-header">
        <h1>{config.description}</h1>
        <div>
          <button className="button" onClick={handleTriggerCheck} disabled={triggering}>
            {triggering ? 'Triggering...' : 'Run Check'}
          </button>
          <Link className="button ghost" to="/dashboard">Back to Dashboard</Link>
        </div>
      </header>
      <main className="app-content">
        <p>Website: <a href={config.website_url} target="_blank" rel="noopener noreferrer">{config.website_url}</a></p>

        <h2>Allowed Failures</h2>
        <p>Links listed here will not cause a run to be marked as failed.</p>
        {(config.allowed_failures || []).length > 0 && (
          <ul>
            {(config.allowed_failures || []).map((entry, i) => (
              <li key={i}>
                {entry.url} (Status: {entry.status_code}){' '}
                <button className="button ghost" onClick={() => handleRemoveAllowedFailure(entry)} disabled={allowlistSaving}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
          <input
            type="text"
            placeholder="URL"
            value={newAllowedUrl}
            onChange={e => setNewAllowedUrl(e.target.value)}
            style={{ flex: 1 }}
          />
          <input
            type="number"
            placeholder="Status code"
            value={newAllowedStatusCode}
            onChange={e => setNewAllowedStatusCode(e.target.value)}
            style={{ width: '8rem' }}
          />
          <button className="button" onClick={handleAddAllowedFailure} disabled={allowlistSaving || !newAllowedUrl || !newAllowedStatusCode}>
            {allowlistSaving ? 'Saving...' : 'Add'}
          </button>
        </div>

        <h2>Recent Runs</h2>
        {runs.length === 0 ? (
          <div className="empty-state">
            <p>No runs found for this configuration yet.</p>
          </div>
        ) : (
          <div>
            {runs.map((run) => (
              <RunDetailsComponent key={run.run_id} run={run} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ConfigRunsPage;
